/**
 * Admin Resources Management Page
 * Comprehensive resource review and approval system
 */

import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search, 
  Filter, 
  Download, 
  ExternalLink,
  AlertTriangle,
  User,
  Calendar,
  Tag,
  ThumbsUp,
  ThumbsDown,
  MoreVertical,
  Play,
  Image as ImageIcon
} from 'lucide-react';
import { getPendingResources, getAllResources, updateResourceStatus } from '../services/adminService';

const AdminResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    resourceType: 'all',
    subject: 'all',
    university: 'all'
  });
  const [selectedResource, setSelectedResource] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchResources();
  }, [selectedTab, filters]);

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let resourcesData;
      
      if (selectedTab === 'pending') {
        resourcesData = await getPendingResources({ limitCount: 20 });
      } else if (selectedTab === 'all') {
        resourcesData = await getAllResources({ limitCount: 20 });
      } else {
        // For approved/rejected, use getAllResources with status filter
        resourcesData = await getAllResources({ 
          status: selectedTab === 'approved' ? 'active' : 'rejected',
          limitCount: 20 
        });
      }

      setResources(resourcesData.resources || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setError('Failed to load resources. Please check your permissions and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (resourceId) => {
    try {
      setActionLoading(prev => ({ ...prev, [resourceId]: 'approving' }));
      
      await updateResourceStatus(resourceId, 'active');
      
      // Remove from current list and show success
      setResources(prev => prev.filter(r => r.id !== resourceId));
      setActionLoading(prev => ({ ...prev, [resourceId]: null }));
      
    } catch (error) {
      console.error('Error approving resource:', error);
      setActionLoading(prev => ({ ...prev, [resourceId]: null }));
      setError('Failed to approve resource. Please try again.');
    }
  };

  const handleReject = async (resourceId, reason = '') => {
    try {
      setActionLoading(prev => ({ ...prev, [resourceId]: 'rejecting' }));
      
      await updateResourceStatus(resourceId, 'rejected', reason);
      
      // Remove from current list and show success
      setResources(prev => prev.filter(r => r.id !== resourceId));
      setActionLoading(prev => ({ ...prev, [resourceId]: null }));
      
    } catch (error) {
      console.error('Error rejecting resource:', error);
      setActionLoading(prev => ({ ...prev, [resourceId]: null }));
      setError('Failed to reject resource. Please try again.');
    }
  };

  const handlePreview = (resource) => {
    setSelectedResource(resource);
    setShowPreviewModal(true);
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'video': return <Play className="h-5 w-5" />;
      case 'image': return <ImageIcon className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Pending</span>;
      case 'active':
        return <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Approved</span>;
      case 'rejected':
        return <span className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Rejected</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded-full">Unknown</span>;
    }
  };

  const filteredResources = resources.filter(resource => {
    if (searchQuery && !resource.title.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !resource.subject.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filters.resourceType !== 'all' && resource.resourceType !== filters.resourceType) {
      return false;
    }
    if (filters.subject !== 'all' && resource.subject !== filters.subject) {
      return false;
    }
    return true;
  });

  const tabs = [
    { id: 'pending', name: 'Pending Review', count: resources.filter(r => r.status === 'pending').length },
    { id: 'approved', name: 'Approved', count: resources.filter(r => r.status === 'active').length },
    { id: 'rejected', name: 'Rejected', count: resources.filter(r => r.status === 'rejected').length },
    { id: 'all', name: 'All Resources', count: resources.length }
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Resource Management</h1>
          <p className="text-gray-600 mt-1">Review and moderate academic resources</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
            <Download size={16} />
            <span>Export Data</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-600">{resources.filter(r => r.status === 'pending').length}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-green-600">{resources.filter(r => r.status === 'active').length}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-red-600">{resources.filter(r => r.status === 'rejected').length}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Resources</p>
              <p className="text-2xl font-bold text-gray-900">{resources.length}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search resources by title or subject..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <select
            value={filters.resourceType}
            onChange={(e) => setFilters(prev => ({ ...prev, resourceType: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="document">Documents</option>
            <option value="video">Videos</option>
            <option value="image">Images</option>
          </select>

          <select
            value={filters.subject}
            onChange={(e) => setFilters(prev => ({ ...prev, subject: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">All Subjects</option>
            <option value="Computer Science">Computer Science</option>
            <option value="Mathematics">Mathematics</option>
            <option value="Chemistry">Chemistry</option>
            <option value="Finance">Finance</option>
          </select>

          <select
            value={filters.university}
            onChange={(e) => setFilters(prev => ({ ...prev, university: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            <option value="all">All Universities</option>
            <option value="University of Cape Town">UCT</option>
            <option value="Stellenbosch University">Stellenbosch</option>
            <option value="University of the Witwatersrand">Wits</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                selectedTab === tab.id
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Resources List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-md border border-gray-200 animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </div>
                <div className="h-8 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700">{error}</p>
        </div>
      ) : filteredResources.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No resources found</h3>
          <p className="text-gray-600">No resources match your current filters.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredResources.map((resource) => (
            <div key={resource.id} className="bg-white p-4 sm:p-6 rounded-lg shadow-md border border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <div className="bg-green-100 p-2 rounded-lg">
                        {getResourceIcon(resource.resourceType)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{resource.title}</h3>
                        {getStatusBadge(resource.status)}
                      </div>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">{resource.description}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <User className="h-4 w-4" />
                          <span>{resource.submittedByName}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Tag className="h-4 w-4" />
                          <span>{resource.subject}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="h-4 w-4" />
                          <span>{resource.createdAt.toLocaleDateString()}</span>
                        </div>
                        {resource.fileSize && (
                          <span className="bg-gray-100 px-2 py-1 rounded text-xs">{resource.fileSize}</span>
                        )}
                        {resource.duration && (
                          <span className="bg-gray-100 px-2 py-1 rounded text-xs">{resource.duration}</span>
                        )}
                      </div>

                      {resource.status === 'active' && (
                        <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Eye className="h-4 w-4" />
                            <span>{resource.views} views</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <ThumbsUp className="h-4 w-4" />
                            <span>{resource.likes}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <ThumbsDown className="h-4 w-4" />
                            <span>{resource.dislikes}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <button
                    onClick={() => handlePreview(resource)}
                    className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Preview"
                  >
                    <Eye size={18} />
                  </button>
                  
                  <a
                    href={resource.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Open Resource"
                  >
                    <ExternalLink size={18} />
                  </a>

                  {resource.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(resource.id)}
                        disabled={actionLoading[resource.id]}
                        className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50"
                      >
                        {actionLoading[resource.id] === 'approving' ? 'Approving...' : 'Approve'}
                      </button>
                      
                      <button
                        onClick={() => handleReject(resource.id)}
                        disabled={actionLoading[resource.id]}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50"
                      >
                        {actionLoading[resource.id] === 'rejecting' ? 'Rejecting...' : 'Reject'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedResource && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Resource Preview</h2>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle size={24} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">{selectedResource.title}</h3>
                  <p className="text-gray-600 mt-1">{selectedResource.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Subject:</span> {selectedResource.subject}
                  </div>
                  <div>
                    <span className="font-medium">Type:</span> {selectedResource.resourceType}
                  </div>
                  <div>
                    <span className="font-medium">Submitted by:</span> {selectedResource.submittedByName}
                  </div>
                  <div>
                    <span className="font-medium">University:</span> {selectedResource.submittedByUniversity}
                  </div>
                </div>

                {selectedResource.resourceType === 'video' && selectedResource.thumbnailUrl && (
                  <div className="mt-4">
                    <img 
                      src={selectedResource.thumbnailUrl} 
                      alt="Video thumbnail"
                      className="w-full max-w-md mx-auto rounded-lg"
                    />
                  </div>
                )}
                
                <div className="flex space-x-3 pt-4 border-t">
                  <a
                    href={selectedResource.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <ExternalLink size={16} />
                    <span>Open Resource</span>
                  </a>
                  
                  {selectedResource.status === 'pending' && (
                    <>
                      <button
                        onClick={() => {
                          handleApprove(selectedResource.id);
                          setShowPreviewModal(false);
                        }}
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        Approve
                      </button>
                      
                      <button
                        onClick={() => {
                          handleReject(selectedResource.id);
                          setShowPreviewModal(false);
                        }}
                        className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResourcesPage;